/**
 * DELIVERY UI - Main delivery display component and utilities
 * 
 * This file contains:
 * - DeliveryDisplay component (main display component)
 * - Event listeners that call centralized drawer opening functions
 * - updateDeliveryDisplay function for updating delivery display content
 * 
 * Note: Individual drawer components have been moved to separate files:
 * - DeliveryDrawer.js - Delivery times drawer
 * - PostalCodeDrawer.js - Postal code drawer
 * 
 * All drawer opening logic is centralized in delivery-core.js
 */


// Constants
const SELECTORS = {
  POSTCODE_BUTTON: '#open-postcode-drawer',
  TIMESLOT_CARDS: '.delivery-time-slot-card',
  DELIVERY_DRAWER: 'delivery-times-drawer-component',
  DELIVERY_DISPLAY: 'custom-drawer-component',
  DRAWER_CONTENT: '.delivery-times-drawer__content'
};

const CONSOLE_PREFIX = '[DELIVERY-DISPLAY]';

// Helper Functions
/**
 * @param {any} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

/**
 * Get element by selector with type safety
 * @param {HTMLElement} context - The context element to search within
 * @param {string} selector - CSS selector
 * @returns {HTMLElement|null} - The element or null
 */
function getElement(context, selector) {
  const element = context.querySelector(selector);
  return element instanceof HTMLElement ? element : null;
}

/**
 * Get all elements by selector with type safety
 * @param {HTMLElement} context - The context element to search within
 * @param {string} selector - CSS selector
 * @returns {HTMLElement[]} - Array of elements
 */
function getElements(context, selector) {
  return Array.from(context.querySelectorAll(selector)).filter(el => el instanceof HTMLElement);
}

/**
 * Remove existing event listeners by cloning element
 * @param {HTMLElement} element - The element to clone
 * @returns {HTMLElement} - The cloned element with listeners removed
 */
function removeExistingListeners(element) {
  const clonedElement = element.cloneNode(true);
  const parentNode = element.parentNode;
  if (parentNode) {
    parentNode.replaceChild(clonedElement, element);
  }
  return /** @type {HTMLElement} */ (clonedElement);
}

/**
 * Open postal code drawer
 */
function openPostalCodeDrawer() {
  if (/** @type {any} */ (window).DeliveryCore?.openPostalCodeDrawer) {
    /** @type {any} */ (window).DeliveryCore.openPostalCodeDrawer();
  }
}

/**
 * Open delivery times drawer
 */
function openDeliveryTimesDrawer() {
  if (/** @type {any} */ (window).DeliveryCore?.openDeliveryTimesDrawer) {
    /** @type {any} */ (window).DeliveryCore.openDeliveryTimesDrawer();
  }
}

/**
 * Fetch updated page content
 * @returns {Promise<Document>} - Parsed HTML document
 */
async function fetchUpdatedPageContent() {
  try {

    const response = await fetch(window.location.href, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!response.ok) {
      const error = new Error(`Failed to fetch updated content: ${response.status} ${response.statusText}`);
      throw error;
    }
    
    const html = await response.text();
    const parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
  } catch (error) {
    throw error;
  }
}

/**
 * Update delivery display component
 * @param {Document} doc - Parsed HTML document
 * @param {string|null} postcode - The new postcode
 */
function updateDeliveryDisplayComponent(doc, postcode) {
  try {
    const newDeliveryDisplay = doc.querySelector(SELECTORS.DELIVERY_DISPLAY);
    const currentDeliveryDisplay = document.querySelector(SELECTORS.DELIVERY_DISPLAY);
    
    if (newDeliveryDisplay && currentDeliveryDisplay) {

      // Update the data attribute with the new postcode if provided
      if (postcode) {
        /** @type {HTMLElement} */ (currentDeliveryDisplay).dataset.currentPostcode = postcode;
      }
      
      // Replace the content while preserving event listeners on the wrapper
      currentDeliveryDisplay.innerHTML = newDeliveryDisplay.innerHTML;
      
      // Re-setup event listeners for the delivery display
      if (/** @type {any} */ (currentDeliveryDisplay).setupEventListeners) {
        /** @type {any} */ (currentDeliveryDisplay).setupEventListeners();
      }
    } else {
      logMessage('warn', 'Delivery display elements not found during update', {
        hasNewDisplay: !!newDeliveryDisplay,
        hasCurrentDisplay: !!currentDeliveryDisplay
      });
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Update delivery times drawer content
 * @param {Document} doc - Parsed HTML document
 */
function updateDeliveryTimesDrawerContent(doc) {
  try {
    const newDrawer = doc.querySelector(SELECTORS.DELIVERY_DRAWER);
    const currentDrawer = document.querySelector(SELECTORS.DELIVERY_DRAWER);
    
    if (newDrawer && currentDrawer) {

      // Update the drawer content
      const newContent = newDrawer.querySelector(SELECTORS.DRAWER_CONTENT);
      const currentContent = currentDrawer.querySelector(SELECTORS.DRAWER_CONTENT);
      
      if (newContent && currentContent) {
        // Set up MutationObserver to watch for content changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              // Re-initialize when content is updated
              if (/** @type {any} */ (currentDrawer).initializeDayTimeslotInteraction) {
                /** @type {any} */ (currentDrawer).initializeDayTimeslotInteraction();
              }
              // Disconnect observer after re-initialization
              observer.disconnect();
            }
          });
        });
        
        // Start observing before updating content
        observer.observe(currentContent, { childList: true, subtree: true });
        
        // Update the content
        currentContent.innerHTML = newContent.innerHTML;
      } else {
        logMessage('warn', 'Drawer content elements not found during update', {
          hasNewContent: !!newContent,
          hasCurrentContent: !!currentContent
        });
      }
    } else {
      logMessage('warn', 'Delivery drawer elements not found during update', {
        hasNewDrawer: !!newDrawer,
        hasCurrentDrawer: !!currentDrawer
      });
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Main delivery display component
 */
export class DeliveryDisplay extends HTMLElement {
  connectedCallback() {
    try {
      this.setupEventListeners();
    } catch (error) {
      throw error;
    }
  }

  setupEventListeners() {
    try {
      // Setup postcode button listener
      this.setupPostcodeButtonListener();
      
      // Setup timeslot card listeners
      this.setupTimeslotCardListeners();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Setup postcode button event listener
   */
  setupPostcodeButtonListener() {
    const postcodeButton = getElement(this, SELECTORS.POSTCODE_BUTTON);
    if (postcodeButton) {
      const newPostcodeButton = removeExistingListeners(postcodeButton);
      newPostcodeButton.addEventListener('click', openPostalCodeDrawer);
    }
  }

  /**
   * Setup timeslot card event listeners
   */
  setupTimeslotCardListeners() {
    const timeslotCards = getElements(this, SELECTORS.TIMESLOT_CARDS);
    timeslotCards.forEach(card => {
      const newCard = removeExistingListeners(card);
      newCard.addEventListener('click', openDeliveryTimesDrawer);
    });
  }
}

// Register custom element
if (!customElements.get('delivery-display')) {
  customElements.define('delivery-display', DeliveryDisplay);
}

/**
 * Get the currently selected delivery day from cart attributes
 * @param {any} [cart] - Optional cart data to avoid redundant API calls
 * @returns {Promise<string|null>} - The selected delivery day or null if none selected
 */
export async function getCurrentDeliveryDay(cart = undefined) {
  try {
    

    // Get delivery day from cart attributes using the centralized function
    if (/** @type {any} */ (window).DeliveryCartAttributes) {
      try {
        const deliveryDay = await /** @type {any} */ (window).DeliveryCartAttributes.getCurrentDeliveryDay(cart);
        
        return deliveryDay;
      } catch (error) {
        logMessage('warn', 'Error getting delivery day from cart attributes:', error);
        return null;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Update delivery display with new content from server
 * @param {string|null} postcode - The new postcode to set (optional)
 */
export async function updateDeliveryDisplay(postcode = null) {
  try {
    

    // Fetch the current page with updated cart attributes
    const doc = await fetchUpdatedPageContent();
    
    // Update delivery display component
    updateDeliveryDisplayComponent(doc, postcode);
    
    // Update delivery times drawer content
    updateDeliveryTimesDrawerContent(doc);
    
  } catch (error) {
    logMessage('error', 'Error updating delivery display:', error);
    // Fallback to page reload if dynamic update fails
    window.location.reload();
  }
}

// Export for global access
if (typeof window !== "undefined") {
  /** @type {any} */ (window).DeliveryUI = {
    DeliveryDisplay,
    getCurrentDeliveryDay,
    updateDeliveryDisplay
  };
}